// HeadlessFacetReport.jsx
//
// GET-navigable, automated Roof Sections Report generator:
//   /generate-facet/:token/:designID  (optionally ?returnUrl=...)
//
// Like the DXF route, this is data-only: the Roof Section geometry is reconstructed
// from currentDesign.model_data (see facetModelAdapter), so the 3D Designer is
// NOT mounted. We temporarily populate the shared global POLYGONS array that
// the unmodified generateFacetReport() reads from, then restore it. Because the
// Designer is not mounted on this route, POLYGONS is otherwise empty/unused, so
// this is side-effect free for the rest of the app.

import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { checkUser, getDesign } from "../../helpers/API";
import { POLYGONS } from "../../ThreeJs/Global/variables";
import { generateFacetReport } from "../../helpers/FacetReport";
import { resolveReportProjectId } from "../../helpers/reportUpload";
import { buildFacetPolygonsFromModelData } from "./facetModelAdapter";
import { persistReportBranding } from "../../helpers/reportBranding";
import {
  HeadlessReportController,
  REPORT_PHASES,
} from "./HeadlessReportController";
import HeadlessReportScreen from "./HeadlessReportScreen";

// Swap the shared POLYGONS contents in place (it is `export let POLYGONS = []`,
// so all importers share the same array instance). Returns the previous items
// so the caller can restore them.
const swapPolygons = (next) => {
  const prev = POLYGONS.splice(0, POLYGONS.length);
  if (Array.isArray(next) && next.length) POLYGONS.push(...next);
  return prev;
};

const HeadlessFacetReport = () => {
  const { token, designID } = useParams();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl") || "";

  const [status, setStatus] = useState(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const controller = new HeadlessReportController({
      reportType: "facet",
      returnUrl,
      onChange: setStatus,
    });

    const run = async () => {
      if (!token || !designID) {
        controller.fail("missing-params", "Token or design ID missing from URL.");
        return;
      }

      // 1) Auth
      controller.setPhase(REPORT_PHASES.AUTHENTICATING, { progress: 10 });
      try {
        const res = await checkUser(token);
        if (res?.status !== 200) {
          controller.fail("unauthenticated", "User verification failed.");
          return;
        }
        persistReportBranding(res?.data?.data || res?.data || {});
      } catch (e) {
        controller.fail("unauthenticated", "User verification failed.");
        return;
      }

      // 2) Fetch design
      controller.setPhase(REPORT_PHASES.LOADING_DESIGN, { progress: 30 });
      let currentDesign;
      try {
        const res = await getDesign(token, designID);
        if (res?.status !== 200 || !res?.data?.data) {
          controller.fail("design-not-found", "Design could not be retrieved.");
          return;
        }
        currentDesign = res.data.data;
      } catch (e) {
        controller.fail("design-not-found", "Design could not be retrieved.");
        return;
      }

      // 3) Reconstruct roof section polygons from saved model_data
      controller.setPhase(REPORT_PHASES.GENERATING, { progress: 60 });
      let facetPolygons;
      try {
        facetPolygons = buildFacetPolygonsFromModelData(currentDesign.model_data);
      } catch (e) {
        controller.fail("invalid-model-data", "model_data is not valid JSON.");
        return;
      }

      if (!Array.isArray(facetPolygons) || facetPolygons.length === 0) {
        controller.fail("no-segments", "No sections found to generate a Roof Sections Report.");
        return;
      }

      // 4) Generate + upload via the unmodified generateFacetReport.
      // It reads the global POLYGONS, so we temporarily install our
      // reconstructed polygons and restore afterwards.
      const prevPolygons = swapPolygons(facetPolygons);
      try {
        await generateFacetReport(currentDesign, {
          auth: { token: token || "" },
          project: { id: resolveReportProjectId(currentDesign, designID) },
          // Suppress the browser save dialog on this headless route; the PDF is
          // delivered via upload + redirect only.
          shouldDownload: false,
        });
        controller.succeed("Roof Sections Report generated and uploaded.");
      } catch (e) {
        console.error("Headless Roof Sections Report generation failed:", e);
        controller.fail("generation-failed", "Roof Sections Report generation threw an error.");
      } finally {
        // Restore whatever was there before (normally nothing on this route).
        swapPolygons(prevPolygons);
      }
    };

    run();
  }, [token, designID, returnUrl]);

  return <HeadlessReportScreen reportType="facet" status={status} />;
};

export default HeadlessFacetReport;
